import { QueryClient } from '@tanstack/react-query';

import {
  normalizeAdminChatListItem,
  normalizeAdminSummary,
  normalizeChatMessage,
  normalizePatientChatThread,
  type ApiAdminChatListItem,
  type ApiChatMessage,
  type ApiPatientChatThread,
} from '../../../api/chats';
import { buildWebSocketUrl } from '../../../api/client';
import { env } from '../../../config/env';
import type { ApiChatParticipantSummary } from '../../../api/chats';
import type { ChatMessage, PatientChatThread } from '../../../types/admin';
import type { AdminChatListItem } from '../../../types/admin';

type ChatRealtimeEventType =
  | 'chat.thread.snapshot'
  | 'chat.message.created'
  | 'chat.message.edited'
  | 'chat.message.deleted'
  | 'chat.message.reactions_updated'
  | 'chat.read.updated'
  | 'chat.assignments.updated'
  | 'chat.typing';

export type ChatTypingPayload = {
  readonly actor_role: 'patient' | 'admin';
  readonly actor_id: string;
  readonly is_typing: boolean;
};

type ApiChatThreadMeta = {
  readonly patient_id: string;
  readonly unread_count: number;
  readonly manually_unread?: boolean;
  readonly assigned_admins: readonly ApiChatParticipantSummary[];
  readonly last_message_at?: string | null;
  readonly last_message_preview?: string | null;
};

export type AdminChatRealtimeEvent = {
  readonly type: ChatRealtimeEventType;
  readonly patient_id: string;
  readonly occurred_at?: string;
  readonly payload: {
    readonly chat_list_item?: ApiAdminChatListItem | null;
    readonly thread?: ApiPatientChatThread | null;
    readonly thread_meta?: ApiChatThreadMeta | null;
    readonly message?: ApiChatMessage | null;
    readonly message_id?: string | null;
    readonly is_visible?: boolean;
    readonly typing?: ChatTypingPayload;
  };
};

/**
 * Appends [incoming], reconciling an optimistic `tmp-*` placeholder (matched by
 * sender + text) and de-duplicating by id.
 */
function mergeCreatedMessage(
  messages: readonly ChatMessage[],
  incoming: ChatMessage,
): readonly ChatMessage[] {
  if (messages.some((message) => message.id === incoming.id)) {
    return messages;
  }
  const optimisticIndex = messages.findIndex(
    (message) =>
      message.id.startsWith('tmp-') &&
      message.sender_user_id === incoming.sender_user_id &&
      message.message_text === incoming.message_text &&
      message.message_type === incoming.message_type,
  );
  if (optimisticIndex >= 0) {
    const next = [...messages];
    next[optimisticIndex] = incoming;
    return next;
  }
  return [...messages, incoming];
}

export function buildAdminChatWebSocketUrl(accessToken: string, patientId: string): string {
  return buildWebSocketUrl(env.apiBaseUrl, '/admin/chats/ws', accessToken, {
    patient_id: patientId || undefined,
  });
}

export function applyAdminChatRealtimeEvent(
  queryClient: QueryClient,
  event: AdminChatRealtimeEvent,
): void {
  if (event.type === 'chat.typing') {
    // Typing is ephemeral and handled by useAdminChatRealtime, not the query cache.
    return;
  }

  const { chat_list_item: nextChatPayload, thread_meta: threadMeta, message, message_id } =
    event.payload;
  const nextThreadPayload = event.payload.thread;
  const isVisible = event.payload.is_visible ?? true;

  // --- Roster (inbox list) -------------------------------------------------
  if (nextChatPayload) {
    const nextChat = normalizeAdminChatListItem(nextChatPayload);
    queryClient.setQueriesData<readonly AdminChatListItem[]>(
      { queryKey: ['admin-chats'] },
      (current) => {
        const existing = current ?? [];
        const otherChats = existing.filter((chat) => chat.patient_id !== nextChat.patient_id);
        return [...otherChats, nextChat].sort((left, right) =>
          right.last_message_at.localeCompare(left.last_message_at),
        );
      },
    );
  } else if (!isVisible) {
    queryClient.setQueriesData<readonly AdminChatListItem[]>(
      { queryKey: ['admin-chats'] },
      (current) => (current ?? []).filter((chat) => chat.patient_id !== event.patient_id),
    );
  }

  // --- Selected thread window ---------------------------------------------
  const threadKey = ['patient-chat', event.patient_id];

  if (!isVisible) {
    queryClient.setQueryData<PatientChatThread | null>(threadKey, null);
    return;
  }

  // The connect/reconnect snapshot is the only event allowed to replace the
  // whole loaded window (it carries the latest page).
  if (event.type === 'chat.thread.snapshot' && nextThreadPayload) {
    queryClient.setQueryData<PatientChatThread>(
      threadKey,
      normalizePatientChatThread(nextThreadPayload),
    );
    return;
  }

  queryClient.setQueryData<PatientChatThread | null>(threadKey, (current) => {
    if (!current) {
      // No window loaded yet — seed from a full thread payload if one arrives.
      return nextThreadPayload ? normalizePatientChatThread(nextThreadPayload) : current ?? null;
    }

    let messages = current.messages;
    if (event.type === 'chat.message.created' && message) {
      // Only append when the window is the live tail; otherwise the message is
      // beyond the loaded window and the meta update below is enough.
      if (!current.page?.has_more_after) {
        messages = mergeCreatedMessage(messages, normalizeChatMessage(message, event.patient_id));
      }
    } else if ((event.type === 'chat.message.edited' || event.type === 'chat.message.reactions_updated') && message) {
      const updated = normalizeChatMessage(message, event.patient_id);
      messages = messages.map((existing) => (existing.id === updated.id ? updated : existing));
    } else if (event.type === 'chat.message.deleted' && message_id) {
      messages = messages.filter((existing) => existing.id !== message_id);
    }

    let next: PatientChatThread = messages === current.messages ? current : { ...current, messages };
    if (threadMeta) {
      next = {
        ...next,
        unread_count: threadMeta.unread_count,
        manually_unread: threadMeta.manually_unread ?? false,
        assigned_admins: threadMeta.assigned_admins.map(normalizeAdminSummary),
        last_message_at: threadMeta.last_message_at ?? next.last_message_at,
        last_message_preview: threadMeta.last_message_preview ?? next.last_message_preview,
      };
    }
    return next;
  });
}
